import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu as MenuIcon, X, MapPin, Heart, User, LogOut, Upload, Settings, ShoppingCart, Home, Video, Sparkles, Calendar, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import AuthPopover from '@/components/auth/AuthPopover';
import AuthDialog from '@/components/auth/AuthDialog';
import { useToast } from '@/hooks/use-toast';
import { EnhancedSearchPopup } from '@/components/search/EnhancedSearchPopup';
import { CartButton } from '@/components/cart/CartButton';
import SequencesMegaMenu from './SequencesMegaMenu';
import { Logo } from '@/components/ui/Logo';

const NavBar = () => {
  const [isScrolled, setIsScrolled] = useState(true); // Always start with true
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isAdmin = user?.email === 'joey@towb.in';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(true); // Always keep it true
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [navigate]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    await logout();
    toast({
      title: "Signed out",
      description: "You have been signed out successfully.",
    });
    setIsMobileMenuOpen(false);
  };


  const handleFavoriteClick = () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please sign in to view your favorites",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }
    
    navigate('/favorites');
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header 
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out",
          "bg-background/80 backdrop-blur-md shadow-xs",
          isMobile ? "px-4 py-3" : "px-6 py-4"
        )}
      >
        {/* Mobile Header */}
        {isMobile ? (
          <div className="flex items-center justify-between">
            {/* Hamburger Menu */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full -ml-2"
              onClick={toggleMobileMenu}
            >
              {isMobileMenuOpen ? <X size={32} /> : <MenuIcon size={32} />}
            </Button>

            {/* Logo - centered */}
            <Link 
              to="/" 
              className="text-xl font-bold flex items-center space-x-2 group absolute left-1/2 transform -translate-x-1/2"
            >
              <Logo />
            </Link>

            {/* Right side actions */}
            <div className="flex items-center space-x-2">
              {/* Enhanced Search */}
              <EnhancedSearchPopup open={searchOpen} onOpenChange={setSearchOpen}>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Search size={20} />
                </Button>
              </EnhancedSearchPopup>

              {/* Sign Up/Profile Button */}
              {isAuthenticated ? (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-full"
                  onClick={() => navigate('/profile')}
                >
                  <User size={20} />
                </Button>
              ) : (
                <AuthDialog 
                  triggerClassName="h-9 px-3 text-sm font-medium"
                  triggerText="Sign Up"
                  initialView="signUp"
                />
              )}
            </div>
          </div>
        ) : (
          /* Desktop Header */
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link 
              to="/" 
              className="text-2xl font-bold flex items-center space-x-2 group"
            >
              <Logo />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/displays" className="text-foreground hover:text-primary font-medium transition-colors">
                Displays
              </Link>
              <div className="text-foreground hover:text-primary font-medium transition-colors">
                <SequencesMegaMenu />
              </div>
              <Link to="/videos" className="text-foreground hover:text-primary font-medium transition-colors">
                Videos
              </Link>
            </nav>

            {/* Desktop Search and Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <EnhancedSearchPopup open={searchOpen} onOpenChange={setSearchOpen}>
                <Button variant="outline" size="icon" className="rounded-full">
                  <Search size={18} />
                </Button>
              </EnhancedSearchPopup>
              
              {/* Upload Button for authenticated users */}
              {isAuthenticated && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full px-3"
                  onClick={() => navigate('/upload-sequence')}
                >
                  <Upload size={16} className="mr-1" />
                  Upload
                </Button>
              )}
              
              {/* Admin Button for admin user */}
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full px-3"
                  onClick={() => navigate('/admin')}
                >
                  <Settings size={16} className="mr-1" />
                  Admin
                </Button>
              )}
              
              <Button 
                variant="outline" 
                size="icon" 
                className="rounded-full" 
                onClick={() => navigate('/displays')}
              >
                <MapPin size={18} />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="rounded-full"
                onClick={handleFavoriteClick}
              >
                <Heart size={18} />
              </Button>
              <CartButton />
              <AuthPopover />
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu Overlay */}
      {isMobile && (
        <>
          {/* Backdrop */}
          <div 
            className={cn(
              "fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300",
              isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Slide-out Menu */}
          <div 
            className={cn(
              "fixed left-0 top-0 h-full w-[280px] bg-background z-50 md:hidden transition-transform duration-300 ease-in-out shadow-xl",
              isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            )}
          >
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <Logo />
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X size={20} />
              </Button>
            </div>

            {/* Menu Content */}
            <div className="flex flex-col h-[calc(100%-73px)]">
              <nav className="flex-1 overflow-y-auto">
                {/* Main Navigation */}
                <div className="p-4 space-y-1">
                  <Link 
                    to="/" 
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Home size={20} />
                    <span className="font-medium">Home</span>
                  </Link>
                  <Link 
                    to="/displays" 
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <MapPin size={20} />
                    <span className="font-medium">Displays</span>
                  </Link>
                  <Link 
                    to="/sequences" 
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Sparkles size={20} />
                    <span className="font-medium">Sequences</span>
                  </Link>
                  <Link 
                    to="/videos" 
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Video size={20} />
                    <span className="font-medium">Videos</span>
                  </Link>
                </div>

                <div className="border-t">
                  <div className="p-4 space-y-1">
                    <button 
                      className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors w-full text-left"
                      onClick={handleFavoriteClick}
                    >
                      <Heart size={20} />
                      <span className="font-medium">Favorites</span>
                    </button>
                    <Link 
                      to="/cart" 
                      className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <ShoppingCart size={20} />
                      <span className="font-medium">Cart</span>
                    </Link>
                  </div>
                </div>

                {/* User Section */}
                {isAuthenticated && (
                  <div className="border-t">
                    <div className="p-4 space-y-1">
                      <Link 
                        to="/profile" 
                        className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <User size={20} />
                        <span className="font-medium">Profile</span>
                      </Link>
                      <Link 
                        to="/upload-sequence" 
                        className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Upload size={20} />
                        <span className="font-medium">Upload Sequence</span>
                      </Link>
                      <Link 
                        to="/seller-dashboard" 
                        className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Store size={20} />
                        <span className="font-medium">Seller Dashboard</span>
                      </Link>
                      {isAdmin && (
                        <Link 
                          to="/admin" 
                          className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Settings size={20} />
                          <span className="font-medium">Admin</span>
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </nav>

              {/* Bottom Actions */}
              <div className="p-4 border-t">
                {isAuthenticated ? (
                  <Button 
                    variant="default" 
                    className="w-full"
                    onClick={handleSignOut}
                  >
                    <LogOut size={18} className="mr-2" /> Sign Out
                  </Button>
                ) : (
                  <AuthDialog triggerClassName="w-full" triggerText="Sign Up" initialView="signUp" />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NavBar;
